/**
 * Pre-Performance Priming — a mental warm-up tool for key moments.
 *
 * Flow
 *   /networker/prime                       → Context step
 *   /networker/prime/curate                → Curate affirmations
 *   /networker/prime/session/:sessionId    → Priming audio session
 *
 * This wraps the Instant Alignment pipeline. The difference is framing:
 * the user supplies the SPECIFIC moment (a call, pitch, match, meeting)
 * and curates affirmations before the system generates and auto-saves.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, Loader2, Plus, Volume2, VolumeX, X, Sparkles } from "lucide-react";
import { voiceVolume, getStoredAudioMode } from "../../utils/modeAudio";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem("reclaim_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Context-pack lookup. The `?from=` query param + a session-storage flag
// (set on entry) drive the copy + the back-button destination so that
// entering Pre-Performance Priming via the Golf intro feels like a golf
// flow, via Aurum feels like an Aurum flow, and via Networker keeps the
// original networker call/meeting framing.
const CONTEXT_PACKS = {
  golf: {
    storageKey: "golf_prime_entry",
    eyebrow: "Pre-Performance Priming",
    title: "What's the round?",
    blurb:
      "Tell me about the round, lesson, match or competition you're preparing for. I'll curate the exact affirmations to anchor it.",
    nameLabel: "Name this round",
    namePlaceholder: "e.g. Saturday medal · East Course",
    contextLabel: "What do you want to bring into it?",
    contextPlaceholder:
      "Notes about the course, the conditions, who you're playing with, how you want to feel, the shot you're picturing. One short paragraph is enough.",
    contextPrefix: "Round",
    backTo: "/golf",
  },
  aurum: {
    storageKey: "aurum_prime_entry",
    eyebrow: "Pre-Performance Priming",
    title: "What's the moment?",
    blurb:
      "Tell me about the call, meeting, pitch or moment you're preparing for. I'll curate the exact affirmations to anchor it.",
    nameLabel: "Name this moment",
    namePlaceholder: "e.g. Call with Tom · Friday",
    contextLabel: "What do you want to bring into it?",
    contextPlaceholder:
      "Notes about who's in the room, what's at stake, how you want to feel, the outcome you'd love. One short paragraph is enough.",
    contextPrefix: "Event",
    backTo: "/aurum-edge",
  },
  networker: {
    storageKey: null,
    eyebrow: "Pre-Performance Priming",
    title: "What's the moment?",
    blurb:
      "Tell me about the call, meeting, pitch or moment you're preparing for. I'll curate the exact affirmations to anchor it.",
    nameLabel: "Name this moment",
    namePlaceholder: "e.g. Call with Tom · Friday",
    contextLabel: "What do you want to bring into it?",
    contextPlaceholder:
      "Notes about who's in the room, what's at stake, how you want to feel, the outcome you'd love. One short paragraph is enough.",
    contextPrefix: "Event",
    backTo: "/networker",
  },
};

// Resolve the active context pack — `?from=` wins, otherwise fall back to
// the most recent sessionStorage entry flag (so the curate + session
// pages downstream still get golf/aurum theming after the user clicks Next).
const resolveContextPack = (searchParams) => {
  const from = (searchParams.get("from") || "").toLowerCase();
  if (CONTEXT_PACKS[from]) return { key: from, pack: CONTEXT_PACKS[from] };
  try {
    if (sessionStorage.getItem("golf_prime_entry") === "1") return { key: "golf", pack: CONTEXT_PACKS.golf };
    if (sessionStorage.getItem("aurum_prime_entry") === "1") return { key: "aurum", pack: CONTEXT_PACKS.aurum };
  } catch (_) { /* noop */ }
  return { key: "networker", pack: CONTEXT_PACKS.networker };
};

/* ─────────────────────────── SHARED SHELL ─────────────────────────── */
function NavyShell({ children, onBack, testId }) {
  return (
    <div
      className="min-h-screen relative overflow-hidden text-white"
      data-testid={testId}
      style={{
        background:
          "radial-gradient(ellipse at 50% 20%, #0E1A2C 0%, #0A1424 35%, #060B16 70%, #03060D 100%)",
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, rgba(217,165,90,0.06) 0%, rgba(217,165,90,0.02) 40%, transparent 75%)",
        }}
      />
      <motion.div
        aria-hidden
        animate={{ opacity: [0.4, 0.65, 0.4] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 70% 30%, rgba(150,180,220,0.06), transparent 55%)",
        }}
      />
      {onBack && (
        <header className="relative z-10 px-5 pt-5 pb-1 flex items-center">
          <button
            onClick={onBack}
            className="p-2 -ml-2 rounded-lg hover:bg-white/[0.04] transition-colors"
            aria-label="Back"
            data-testid={`${testId}-back`}
          >
            <ArrowLeft size={20} className="text-white/55" />
          </button>
        </header>
      )}
      {children}
    </div>
  );
}

/* ────────────────────── STEP 1 — CONTEXT ────────────────────── */
export default function PrePerformancePriming() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [eventName, setEventName] = useState("");
  const [context, setContext] = useState("");
  const [arrived, setArrived] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const { key: contextKey, pack } = useMemo(() => resolveContextPack(searchParams), [searchParams]);

  // Persist the entry context so downstream Curate + Session screens can
  // route back to the originating zone (Golf → /golf, Aurum → /aurum-edge).
  useEffect(() => {
    try {
      const from = (searchParams.get("from") || "").toLowerCase();
      if (from === "golf") {
        sessionStorage.setItem("golf_prime_entry", "1");
        sessionStorage.removeItem("aurum_prime_entry");
      } else if (from === "aurum") {
        sessionStorage.setItem("aurum_prime_entry", "1");
        sessionStorage.removeItem("golf_prime_entry");
      }
    } catch (_) { /* noop */ }
  }, [searchParams]);

  useEffect(() => {
    const t = setTimeout(() => setArrived(true), 80);
    // Restore any in-flight draft so navigation away doesn't lose work
    try {
      const draft = JSON.parse(localStorage.getItem("pre_prime_draft_v1") || "{}");
      if (draft.eventName) setEventName(draft.eventName);
      if (draft.context) setContext(draft.context);
    } catch (_) {}
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("pre_prime_draft_v1", JSON.stringify({ eventName, context }));
    } catch (_) {}
  }, [eventName, context]);

  const handleNext = useCallback(async () => {
    const trimmedEvent = eventName.trim();
    const trimmedContext = context.trim();
    if (!trimmedEvent) { setError("Give this moment a name"); return; }
    if (trimmedContext.length < 10) { setError("Add a little more context — even one sentence helps"); return; }
    setError("");
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/pre-prime/suggest`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ context: `${pack.contextPrefix}: ${trimmedEvent}\n\n${trimmedContext}` }),
      });
      if (!res.ok) {
        let msg = `Suggest failed (${res.status})`;
        try {
          const j = await res.json();
          if (j.detail) {
            if (Array.isArray(j.detail)) {
              // FastAPI validation errors come as an array
              const first = j.detail[0];
              if (first?.msg) msg = `${msg} — ${first.msg}`;
            } else if (typeof j.detail === "string") {
              msg = `${msg} — ${j.detail}`;
            }
          }
        } catch (_) {}
        throw new Error(msg);
      }
      const data = await res.json();
      localStorage.setItem("pre_prime_draft_v1", JSON.stringify({
        eventName: trimmedEvent,
        context: trimmedContext,
        suggestions: data.affirmations || [],
        selected: [...(data.affirmations || []).slice(0, 6)],
      }));
      navigate("/networker/prime/curate");
    } catch (e) {
      setError(e.message || "Could not generate affirmations — try again");
    } finally {
      setGenerating(false);
    }
  }, [eventName, context, navigate]);

  const exitDestination = () => {
    try {
      if (sessionStorage.getItem("golf_prime_entry") === "1") return "/golf";
      if (sessionStorage.getItem("aurum_prime_entry") === "1") return "/aurum-edge";
    } catch (_) { /* noop */ }
    return pack.backTo;
  };

  return (
    <NavyShell onBack={() => navigate(exitDestination())} testId="pre-prime-context-screen">
      <main className="relative z-10 max-w-md mx-auto px-6 pt-8 pb-32">
        <motion.p
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 8 }}
          transition={{ duration: 0.9 }}
          className="text-amber-300/75 text-[10px] tracking-[0.32em] uppercase text-center mb-3 flex items-center justify-center gap-2"
        >
          <Sparkles size={10} /> {pack.eyebrow}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 14 }}
          transition={{ duration: 1.0, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="text-white/95 text-3xl sm:text-4xl tracking-tight text-center"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 400 }}
          data-testid={`pre-prime-title-${contextKey}`}
        >
          {pack.title}
        </motion.h1>
        <motion.div
          aria-hidden initial={{ opacity: 0 }} animate={{ opacity: arrived ? 0.55 : 0 }}
          transition={{ duration: 1.2, delay: 0.35 }}
          className="mx-auto mt-5 h-px w-20"
          style={{ background: "linear-gradient(90deg, transparent, rgba(217,180,120,0.65), transparent)" }}
        />
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: arrived ? 0.55 : 0 }}
          transition={{ duration: 1.0, delay: 0.5 }}
          className="text-white/55 text-sm text-center mt-6 leading-relaxed"
          style={{ fontWeight: 300 }}
        >
          {pack.blurb}
        </motion.p>

        {/* Event name */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 10 }}
          transition={{ duration: 1.0, delay: 0.7 }}
          className="mt-10 space-y-2"
        >
          <label className="block text-white/45 text-[10px] tracking-[0.22em] uppercase">
            {pack.nameLabel}
          </label>
          <input
            type="text"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            placeholder={pack.namePlaceholder}
            className="w-full px-4 py-3 rounded-xl bg-black/30 text-white/90 text-base border border-white/10 focus:border-amber-300/45 focus:outline-none transition-colors"
            data-testid="pre-prime-event-name"
          />
        </motion.div>

        {/* Context */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 10 }}
          transition={{ duration: 1.0, delay: 0.82 }}
          className="mt-6 space-y-2"
        >
          <label className="block text-white/45 text-[10px] tracking-[0.22em] uppercase">
            {pack.contextLabel}
          </label>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={6}
            placeholder={pack.contextPlaceholder}
            className="w-full px-4 py-3 rounded-xl bg-black/30 text-white/90 text-sm border border-white/10 focus:border-amber-300/45 focus:outline-none transition-colors resize-y leading-relaxed"
            style={{ fontSize: "15px", lineHeight: 1.55 }}
            data-testid="pre-prime-context"
          />
        </motion.div>

        {error && (
          <p className="mt-4 text-rose-300/90 text-sm text-center" data-testid="pre-prime-error">
            {error}
          </p>
        )}

        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 14 }}
          transition={{ duration: 1.0, delay: 1.0 }}
          className="mt-10 flex justify-center"
        >
          <button
            onClick={handleNext}
            disabled={generating}
            className="px-10 py-3.5 rounded-full text-white/90 text-sm tracking-[0.2em] uppercase transition-transform active:scale-[0.97] disabled:opacity-60 flex items-center gap-3"
            style={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
              border: "1px solid rgba(217,180,120,0.65)",
              boxShadow: "0 12px 40px -16px rgba(217,180,120,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
              backdropFilter: "blur(8px)",
            }}
            data-testid="pre-prime-continue"
          >
            {generating ? <><Loader2 size={14} className="animate-spin" /> Curating affirmations…</> : "Curate My Affirmations"}
          </button>
        </motion.div>

        <p className="text-center text-white/25 text-[11px] tracking-[0.22em] uppercase mt-10">
          Slow is smooth. Smooth is fast.
        </p>
      </main>
    </NavyShell>
  );
}

/* ──────────────────── STEP 2 — CURATE ──────────────────── */
export function PrePerformanceCurate() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState({ eventName: "", context: "", suggestions: [], selected: [] });
  const [customInput, setCustomInput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [arrived, setArrived] = useState(false);

  useEffect(() => {
    try {
      const d = JSON.parse(localStorage.getItem("pre_prime_draft_v1") || "{}");
      if (!d.suggestions || d.suggestions.length === 0) {
        // No suggestions → send them back to step 1
        navigate("/networker/prime", { replace: true });
        return;
      }
      setDraft({
        eventName: d.eventName || "",
        context: d.context || "",
        suggestions: d.suggestions || [],
        selected: d.selected && d.selected.length ? d.selected : (d.suggestions || []).slice(0, 6),
      });
    } catch (_) {
      navigate("/networker/prime", { replace: true });
    }
    const t = setTimeout(() => setArrived(true), 80);
    return () => clearTimeout(t);
  }, [navigate]);

  // Persist every tick
  useEffect(() => {
    if (!draft.eventName) return;
    try {
      localStorage.setItem("pre_prime_draft_v1", JSON.stringify(draft));
    } catch (_) {}
  }, [draft]);

  const toggle = (aff) => {
    setDraft((d) => {
      const isSelected = d.selected.includes(aff);
      return {
        ...d,
        selected: isSelected ? d.selected.filter((a) => a !== aff) : [...d.selected, aff],
      };
    });
  };

  const addCustom = () => {
    const trimmed = customInput.trim();
    if (!trimmed) return;
    setDraft((d) => ({
      ...d,
      suggestions: [...d.suggestions, trimmed],
      selected: [...d.selected, trimmed],
    }));
    setCustomInput("");
  };

  const start = useCallback(async () => {
    if (draft.selected.length === 0) {
      setError("Pick at least one affirmation");
      return;
    }
    setError("");
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/pre-prime/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({
          event_name: draft.eventName,
          context: draft.context,
          affirmations: draft.selected,
          auto_save: true,
        }),
      });
      if (!res.ok) {
        let msg = `Generate failed (${res.status})`;
        try { const j = await res.json(); if (j.detail) msg = `${msg} — ${j.detail}`; } catch (_) {}
        throw new Error(msg);
      }
      const data = await res.json();
      // Stash result for the session screen
      localStorage.setItem("pre_prime_last_result_v1", JSON.stringify(data));
      localStorage.removeItem("pre_prime_draft_v1");
      navigate(`/networker/prime/session/${encodeURIComponent(data.session_id || "latest")}`);
    } catch (e) {
      setError(e.message || "Could not start the priming");
    } finally {
      setGenerating(false);
    }
  }, [draft, navigate]);

  return (
    <NavyShell onBack={() => navigate("/networker/prime")} testId="pre-prime-curate-screen">
      <main className="relative z-10 max-w-md mx-auto px-6 pt-8 pb-32">
        <motion.p
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 8 }}
          transition={{ duration: 0.9 }}
          className="text-amber-300/75 text-[10px] tracking-[0.32em] uppercase text-center mb-3"
          data-testid="pre-prime-curate"
        >
          {draft.eventName || "Pre-Performance Priming"}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 14 }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="text-white/95 text-3xl text-center"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 400 }}
        >
          Choose what to anchor
        </motion.h1>
        <p className="text-white/45 text-sm text-center mt-4 max-w-sm mx-auto">
          Tap the ones that land. They&apos;ll be layered into your audio.
        </p>

        <div className="mt-8 space-y-2.5" data-testid="pre-prime-affirmation-list">
          {draft.suggestions.map((aff, i) => {
            const selected = draft.selected.includes(aff);
            return (
              <motion.button
                key={`${i}-${aff.slice(0, 20)}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 8 }}
                transition={{ duration: 0.55, delay: 0.2 + i * 0.05 }}
                whileTap={{ scale: 0.985 }}
                onClick={() => toggle(aff)}
                data-testid={`pre-prime-aff-${i}`}
                className="w-full text-left rounded-2xl px-4 py-3.5 flex items-center gap-3 transition-all"
                style={{
                  background: selected
                    ? "linear-gradient(135deg, rgba(217,180,120,0.15), rgba(217,180,120,0.06))"
                    : "rgba(255,255,255,0.035)",
                  border: selected
                    ? "1px solid rgba(217,180,120,0.55)"
                    : "1px solid rgba(255,255,255,0.06)",
                  boxShadow: selected ? "0 8px 24px -12px rgba(217,180,120,0.4)" : "none",
                }}
              >
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
                  style={{
                    background: selected ? "rgba(217,180,120,0.85)" : "rgba(255,255,255,0.05)",
                    border: selected ? "1px solid rgba(245,210,150,1)" : "1px solid rgba(255,255,255,0.15)",
                  }}
                >
                  {selected && <Check size={12} className="text-[#0E1A2C]" strokeWidth={3} />}
                </div>
                <span
                  className="text-sm flex-1"
                  style={{
                    color: selected ? "rgba(255,240,210,0.95)" : "rgba(255,255,255,0.65)",
                    fontStyle: "italic",
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontSize: "17px",
                  }}
                >
                  {aff}
                </span>
              </motion.button>
            );
          })}
        </div>

        {/* Custom add */}
        <div className="mt-6 flex gap-2" data-testid="pre-prime-custom-row">
          <input
            type="text"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
            placeholder="Add your own…"
            className="flex-1 px-3.5 py-2.5 rounded-xl bg-black/30 text-white/90 text-sm border border-white/10 focus:border-amber-300/45 focus:outline-none transition-colors italic"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "15px" }}
            data-testid="pre-prime-custom-input"
          />
          <button
            onClick={addCustom}
            disabled={!customInput.trim()}
            className="px-4 rounded-xl text-white/80 disabled:opacity-40 transition-colors"
            style={{ background: "rgba(217,180,120,0.15)", border: "1px solid rgba(217,180,120,0.4)" }}
            data-testid="pre-prime-custom-add"
            aria-label="Add affirmation"
          >
            <Plus size={16} />
          </button>
        </div>

        <div className="mt-6 text-center text-white/40 text-xs tracking-[0.2em] uppercase">
          {draft.selected.length} selected
        </div>

        {error && <p className="mt-4 text-rose-300/90 text-sm text-center">{error}</p>}

        <div className="mt-8 flex justify-center">
          <button
            onClick={start}
            disabled={generating || draft.selected.length === 0}
            className="px-10 py-3.5 rounded-full text-white/90 text-sm tracking-[0.2em] uppercase transition-transform active:scale-[0.97] disabled:opacity-60 flex items-center gap-3"
            style={{
              background: "linear-gradient(180deg, rgba(217,180,120,0.2), rgba(217,180,120,0.08))",
              border: "1px solid rgba(217,180,120,0.7)",
              boxShadow: "0 14px 44px -14px rgba(217,180,120,0.6)",
            }}
            data-testid="pre-prime-start"
          >
            {generating ? <><Loader2 size={14} className="animate-spin" /> Preparing your priming…</> : "Begin Priming"}
          </button>
        </div>
      </main>
    </NavyShell>
  );
}

/* ──────────────────── STEP 3 — SESSION ──────────────────── */
export function PrePerformanceSession() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);
  const [arrived, setArrived] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [needsTapToStart, setNeedsTapToStart] = useState(false);

  useEffect(() => {
    try {
      const d = JSON.parse(localStorage.getItem("pre_prime_last_result_v1") || "{}");
      if (!d.session_id && !d.audio_url) {
        navigate("/networker/prime", { replace: true });
        return;
      }
      setData(d);
    } catch (_) {
      navigate("/networker/prime", { replace: true });
    }
    const t = setTimeout(() => setArrived(true), 60);
    return () => clearTimeout(t);
  }, [navigate]);

  // Fade-in the audio to the configured voice volume. Extracted so the
  // tap-to-start fallback can reuse it.
  const startFadeIn = (audio) => {
    let vol = 0;
    const voiceTarget = voiceVolume(getStoredAudioMode());
    const fade = setInterval(() => {
      vol += 0.01;
      if (vol >= voiceTarget || !audioRef.current) {
        clearInterval(fade);
        if (audioRef.current) audioRef.current.volume = voiceTarget;
      } else if (audioRef.current) {
        audioRef.current.volume = vol;
      }
    }, 60);
  };

  // User-gesture handler for the "Tap to begin" overlay. Re-attempts
  // audio.play() inside a click handler so iOS Safari allows it.
  const handleTapToStart = () => {
    if (!audioRef.current) return;
    audioRef.current.play().then(() => {
      setNeedsTapToStart(false);
      startFadeIn(audioRef.current);
    }).catch((err) => {
      // Still blocked — surface so the user knows something is up.
      console.warn("[pre-prime] manual play still blocked:", err);
    });
  };

  useEffect(() => {
    if (!data || !data.audio_url) return;
    const src = data.audio_url.startsWith("http") ? data.audio_url : `${API}${data.audio_url}`;
    const audio = new Audio(src);
    audio.loop = true;
    audio.volume = 0;
    audio.playsInline = true;
    audio.preload = "auto";
    audioRef.current = audio;
    audio.play().then(() => {
      setNeedsTapToStart(false);
      startFadeIn(audio);
    }).catch((err) => {
      // iOS Safari blocks autoplay after a navigation gesture chain
      // breaks. Show the tap-to-start overlay so the user can resume
      // audio from a fresh user gesture.
      console.warn("[pre-prime] autoplay blocked, showing tap-to-start:", err);
      setNeedsTapToStart(true);
    });
    const tick = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => {
      clearInterval(tick);
      if (audioRef.current) { try { audioRef.current.pause(); } catch (_) {} audioRef.current = null; }
    };
  }, [data]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  // When the user entered Pre-Performance from the Golf or Aurum tile, we
  // route them back to the originating zone on exit — NOT the main
  // Networker hub. Session-storage flags are set by Step 1 (context page).
  const exitDestination = () => {
    try {
      if (sessionStorage.getItem("golf_prime_entry") === "1") {
        sessionStorage.removeItem("golf_prime_entry");
        return "/golf";
      }
      if (sessionStorage.getItem("aurum_prime_entry") === "1") {
        sessionStorage.removeItem("aurum_prime_entry");
        return "/aurum-edge";
      }
    } catch (_) { /* noop */ }
    return "/networker";
  };

  const handleExit = () => {
    const dest = exitDestination();
    if (audioRef.current) {
      let vol = audioRef.current.volume;
      const fade = setInterval(() => {
        vol -= 0.04;
        if (vol <= 0 || !audioRef.current) {
          clearInterval(fade);
          if (audioRef.current) { try { audioRef.current.pause(); } catch (_) {} audioRef.current = null; }
          navigate(dest);
        } else if (audioRef.current) audioRef.current.volume = vol;
      }, 50);
    } else {
      navigate(dest);
    }
  };

  if (!data) return null;

  return (
    <div
      className="min-h-screen relative overflow-hidden flex flex-col items-center justify-center text-white"
      data-testid="pre-prime-session"
      style={{ background: "radial-gradient(ellipse at 50% 50%, #0A1424 0%, #06101D 50%, #02050B 100%)" }}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.4' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />
      <button
        onClick={handleExit}
        className="absolute top-5 left-5 p-2 rounded-full hover:bg-white/[0.05] transition-colors z-20"
        aria-label="Exit"
        data-testid="pre-prime-session-exit"
      >
        <X size={20} className="text-white/45" />
      </button>
      <button
        onClick={() => setMuted((m) => !m)}
        className="absolute top-5 right-5 p-2 rounded-full hover:bg-white/[0.05] transition-colors z-20"
        aria-label={muted ? "Unmute" : "Mute"}
        data-testid="pre-prime-mute-toggle"
      >
        {muted ? <VolumeX size={20} className="text-white/45" /> : <Volume2 size={20} className="text-white/55" />}
      </button>

      {/* Breathing orb centrepiece */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: arrived ? 1 : 0, scale: arrived ? 1 : 0.8 }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative"
      >
        <motion.div
          animate={{ scale: [1, 1.08, 1], opacity: [0.85, 1, 0.85] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="rounded-full"
          style={{ width: 260, height: 260 }}
        >
          <div
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "radial-gradient(circle at 45% 40%, rgba(255,232,200,0.95) 0%, rgba(225,180,140,0.55) 38%, transparent 72%)",
              boxShadow: "0 0 120px rgba(225,180,140,0.45)",
            }}
          />
        </motion.div>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }} animate={{ opacity: arrived ? 0.7 : 0 }}
        transition={{ duration: 1.2, delay: 0.8 }}
        className="mt-12 text-white/75 text-xl italic text-center px-8 max-w-md"
        style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
      >
        {data.event_name || "Priming in progress"}
      </motion.p>
      <motion.p
        initial={{ opacity: 0 }} animate={{ opacity: arrived ? 0.45 : 0 }}
        transition={{ duration: 1.4, delay: 1.4 }}
        className="mt-3 text-white/45 text-sm"
      >
        {data.affirmation_count ? `${data.affirmation_count} affirmations · ` : ""}Saved to your library
      </motion.p>
      <motion.p
        initial={{ opacity: 0 }} animate={{ opacity: arrived ? 0.4 : 0 }}
        transition={{ duration: 1.4, delay: 2.0 }}
        className="mt-8 text-white/40 text-[11px] tracking-[0.25em] uppercase"
      >
        {fmt(elapsed)} · Breathe
      </motion.p>

      {/* iOS Safari sometimes blocks autoplay after a route change. If
          that happens we surface a tap-to-start overlay so the user can
          resume the audio from a fresh user gesture. */}
      <AnimatePresence>
        {needsTapToStart && (
          <motion.button
            type="button"
            onClick={handleTapToStart}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-[#02050B]/85 backdrop-blur-sm"
            data-testid="pre-prime-tap-to-start"
            aria-label="Tap to begin priming audio"
          >
            <span
              className="text-white/85 text-2xl"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
            >
              Tap to begin
            </span>
            <span className="mt-3 text-white/45 text-[11px] tracking-[0.25em] uppercase">
              iOS blocked autoplay — one tap to resume
            </span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
